'use client';

import Image from 'next/image';
import { useLocale } from 'next-intl';
import {
  ChangeEvent,
  InputHTMLAttributes,
  ReactNode,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
  type Ref,
} from 'react';
import { cn } from '@/lib/utils';
import { Field, FieldError } from '@/components/ui/field';
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from '@/components/ui/input-group';
import { useCanAutoFocus } from '@/hooks/useCanAutoFocus';

type TextInputProps = {
  ref?: Ref<HTMLInputElement>;
  withAutoScroll?: boolean;
  value?: string;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  label?: string;
  placeholder?: string;
  error?: string;
  defaultValue?: string;
  className?: string;
  leftIcon?: ReactNode;
  type?: string;
  showCross?: boolean;
  onRemoveValue?: () => void;
  showCounter?: boolean;
  maxCounterLength?: number;
  showAsterisk?: boolean;
  ltrField?: boolean;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'className' | 'value' | 'onChange' | 'defaultValue'>;

export function TextInput({
  ref,
  withAutoScroll = false,
  label = '',
  error = '',
  className = '',
  type,
  leftIcon,
  showCross = false,
  onRemoveValue,
  showCounter = false,
  showAsterisk = false,
  maxCounterLength = 2000,
  ltrField = false,
  ...props
}: TextInputProps) {
  const locale = useLocale();
  const canAutoFocus = useCanAutoFocus();
  const isLtrInRtl = ltrField && locale === 'he';
  const inputRef = useRef<HTMLInputElement>(null);
  useImperativeHandle(ref, () => inputRef.current!);

  const [ownType, setOwnType] = useState(type || 'text');

  const toggleOwnType = useCallback(() => {
    setOwnType((prev) => (prev === 'password' ? 'text' : 'password'));
  }, []);

  const resolvedValue = String(props.value ?? props.defaultValue ?? '');
  const valueLength = resolvedValue.length;
  const hasValue = valueLength > 0;
  const hasLabel = label.length > 0;
  const hasError = error.length > 0;
  const isPassword = type === 'password';
  const showClear = showCross && hasValue;
  const hasEndAddon = isPassword || showClear;
  const showCounterValue = showCounter && valueLength > maxCounterLength - 100;

  return (
    <Field data-invalid={hasError || undefined} className='gap-[4px] data-[invalid=true]:text-current'>
      <InputGroup
        dir={isLtrInRtl ? 'rtl' : undefined}
        className='h-[50px] rounded-[8px] has-[[data-slot=input-group-control]:focus-visible]:ring-[3px]'
      >
        {leftIcon && (
          <InputGroupAddon align='inline-start' className='ps-[8px] pe-0 py-0'>
            {leftIcon}
          </InputGroupAddon>
        )}
        <InputGroupInput
          onFocus={(e) => {
            props.onFocus?.(e);
            if (withAutoScroll && inputRef.current) {
              const { top } = inputRef.current.getBoundingClientRect();
              const scrollElem = document.querySelector('#scroll-target');
              scrollElem?.scrollTo({
                top: scrollElem.scrollTop + (top - 16),
                behavior: 'smooth',
              });
            }
          }}
          dir={ltrField ? 'ltr' : undefined}
          placeholder={label || props.placeholder}
          aria-invalid={hasError}
          className={cn(
            'peer h-full px-[16px] text-[16px] md:text-[16px]',
            hasLabel ? 'pt-[18px] pb-0 placeholder:text-transparent' : 'py-0',
            leftIcon && (isLtrInRtl ? 'pe-[8px]' : 'ps-[8px]'),
            hasEndAddon && (isLtrInRtl ? 'ps-[8px]' : 'pe-[8px]'),
            isLtrInRtl && 'text-right',
            className,
          )}
          ref={inputRef}
          type={ownType}
          {...props}
          autoFocus={props.autoFocus && canAutoFocus}
        />
        {hasLabel && (
          <span
            className={cn(
              'pointer-events-none absolute text-text-secondary transition-[top,font-size,line-height] duration-100 ease-linear',
              'top-[8px] text-[12px] leading-[14.4px]',
              'peer-placeholder-shown:top-[15px] peer-placeholder-shown:text-[16px] peer-placeholder-shown:leading-[20px]',
              'peer-focus:top-[8px] peer-focus:text-[12px] peer-focus:leading-[14.4px]',
              leftIcon ? 'start-[40px]' : 'start-[17px]',
            )}
          >
            {label}
            {showAsterisk && <span className='text-error'> *</span>}
          </span>
        )}
        {!hasLabel && showAsterisk && (
          <InputGroupAddon align='inline-end' className='text-error'>
            *
          </InputGroupAddon>
        )}
        {hasEndAddon && (
          <InputGroupAddon align='inline-end' className='pe-[10px] py-0'>
            {isPassword && (
              <InputGroupButton
                size='icon-sm'
                className='hover:bg-transparent active:scale-100'
                onClick={toggleOwnType}
              >
                <Image
                  src={ownType === 'password' ? '/assets/icons/eye-slash.svg' : '/assets/icons/eye.svg'}
                  height={24}
                  width={24}
                  alt=''
                />
              </InputGroupButton>
            )}
            {showClear && (
              <InputGroupButton
                size='icon-sm'
                className='hover:bg-transparent active:scale-100'
                onClick={() => onRemoveValue?.()}
              >
                <Image src='/assets/icons/close-grey.svg' height={24} width={24} alt='' />
              </InputGroupButton>
            )}
          </InputGroupAddon>
        )}
      </InputGroup>

      {(hasError || showCounterValue) && (
        <div className='mt-[12px] flex items-center gap-[22px] md:mt-0'>
          {hasError && <FieldError className='error-form text-[14px] leading-[20px] text-red-day'>{error}</FieldError>}
          {showCounterValue && (
            <div
              className={cn('text-[14px] leading-[20px] text-text-primary', {
                'text-red-day': valueLength > maxCounterLength,
              })}
            >
              {valueLength} / {maxCounterLength}
            </div>
          )}
        </div>
      )}
    </Field>
  );
}

TextInput.displayName = 'TextInput';
